import { graphql, useStaticQuery } from 'gatsby'
import { useLangContext } from './lang.context'

function useMenu() {
  const { lang } = useLangContext()

  const query = useStaticQuery(graphql`
    query {
      transportKraj: allContentfulTransportKraj(sort: { fields: name }) {
        edges {
          node {
            name
            slug
            node_locale
          }
        }
      }

      transportIntermodal: allContentfulTransportIntermodal(sort: { fields: name }) {
        edges {
          node {
            name
            slug
            node_locale
          }
        }
      }

      transportBranza: allContentfulTransportBranza(
        filter: { higherorder: { eq: true } }
        sort: { fields: name }
      ) {
        edges {
          node {
            name
            slug
            node_locale
            links {
              name
              slug
            }
          }
        }
      }

      transportRodzaj: allContentfulTransportRodzaj(sort: { fields: name }) {
        edges {
          node {
            name
            slug
            node_locale
          }
        }
      }

      spedycja: allContentfulSpedycja(sort: { fields: name }) {
        edges {
          node {
            name
            slug
            node_locale
            noCreatePage
          }
        }
      }

      logistyka: allContentfulLogistyka(sort: { fields: name }) {
        edges {
          node {
            name
            slug
            node_locale
          }
        }
      }

      blogs: allContentfulBlog(sort: { fields: date, order: DESC }) {
        edges {
          node {
            title
            seo_title
            blog_slug
            node_locale
          }
        }
      }

      docs: allContentfulDokument(sort: { fields: title }) {
        edges {
          node {
            title
            slug
            node_locale
          }
        }
      }

      csr: allContentfulCsr(sort: { fields: subtitle }) {
        edges {
          node {
            title
            slug
            node_locale
          }
        }
      }

      achievements: allContentfulNagroda(sort: { fields: title }) {
        edges {
          node {
            title
            slug
            node_locale
          }
        }
      }

      carriers: allContentfulPrzewoznik(sort: { fields: title, order: DESC }) {
        edges {
          node {
            title
            blog_slug
            node_locale
          }
        }
      }
    }
  `)

  const linksCountries = query.transportKraj.edges
    .filter(({ node }) => node.node_locale === 'pl')
    .filter(({ node }) => node.slug !== 'transport-krajowy')
    .map(({ node: { name, slug } }) => ({
      name,
      slug: '/' + slug + '/',
    }))

  const linksIntermodal = query.transportIntermodal.edges
  .filter(({ node }) => node.node_locale === 'pl')
  .map(({ node: { name, slug } }) => ({
    name,
    slug: '/' + slug + '/',
  }))    

  const linksIndustires = query.transportBranza.edges
    .filter(({ node }) => node.node_locale === 'pl')
    .map(({ node: { name, slug, links } }) => ({
      name,
      slug: '/' + slug + '/',
      links: links?.map((link) => ({ ...link, slug: '/' + link.slug + '/' })),
    }))

  const linksTransport = [
    { name: 'Paperliner', slug: '/paperliner/' },
    { name: 'Omida Trade', slug: '/trade/' },
  ].concat(
    query.transportRodzaj.edges
      .filter(({ node }) => node.node_locale === 'pl')
      .map(({ node: { name, slug } }) => ({
        name,
        slug: '/' + slug + '/',
      }))
  )

  const linksSpedition = query.spedycja.edges
    .filter(({ node }) => node.node_locale === 'pl')
    .filter(({ node }) => !node.noCreatePage)
    .map(({ node: { name, slug } }) => ({
      name,
      slug: '/' + slug + '/',
    }))

  const linksLogistics = [
    { name: 'E-commerce', slug: '/ecommerce/' },
    {
      name: lang === 'pl' ? 'Logistyka Kontraktowa' : 'Contract Logistics',
      slug: '/logistyka-kontraktowa/',
    },
  ].concat(
    query.logistyka.edges
      .filter(({ node }) => node.node_locale === 'pl')
      .map(({ node: { name, slug } }) => ({
        name,
        slug: '/' + slug + '/',
      }))
  )

  const linksBlogs = query.blogs.edges
    .filter(({ node }) => node.node_locale === 'pl')
    .filter(({ node }) => node.title || node.seo_title)
    .filter((_, id) => id < 9)
    .map(({ node: { title, seo_title, blog_slug } }) => {
      const newtitle = title || seo_title
      return {
        name: newtitle.length > 50 ? newtitle?.slice(0, 50) + '...' : newtitle,
        slug: '/blog/' + blog_slug + '/',
      }
    })
    .concat({
      name: lang === 'pl' ? '...więcej artykułów' : '...more articles',
      slug: '/blog/',
    })

  // const linksBlogsFooter = query.blogs.edges
  //   .filter(({ node }) => node.node_locale === 'pl')
  //   .filter(({ node }) => node.title || node.seo_title)
  //   // .filter((_, id) => id < 10)
  //   .map(({ node: { title, seo_title, blog_slug } }) => {
  //     const newtitle = title || seo_title
  //     return {
  //       name: newtitle,
  //       slug: '/blog/' + blog_slug + '/',
  //     }
  //   })

  const linksCsr = query.csr.edges
    .filter(({ node }) => node.node_locale === 'pl')
    .map(({ node: { title, slug } }) => ({
      name: title,
      slug: '/csr/' + slug + '/',
    }))

  const linksAchievements = query.achievements.edges
  .filter(({ node }) => node.node_locale === 'pl')
  .map(({ node: { title, slug } }) => ({
    name: title,
    slug: '/nagrody/' + slug + '/',
  }))

  const linksCarrier = query.carriers.edges
  .filter(({ node }) => node.node_locale === 'pl')
  .map(({ node: { title, blog_slug } }) => ({
    name: title,
    slug: '/przewoznik/' + blog_slug + '/',
  }))

  const additionalItem = { name: 'Strefa przewoźnika', slug: '/przewoznik/' };
  const updatedLinksCarrier = [additionalItem, ...linksCarrier];

  const menu = [
    {
      slug: '/kariera/',
      title: {
        pl: 'Kariera',
        en: 'Career',
      },
    },
    {
      slug: '/transport/',
      title: {
        pl: 'Transport',
        en: 'Transport',
      },
      links: [
        {
          slug: '/transport-intermodalny/',
          title: {
            pl: 'Transport Intermodalny',
            en: 'Intermodal Transport',
          },
          links: linksIntermodal,
        },
        {
          slug: '/transport-miedzynarodowy/',
          title: {
            pl: 'Transport Międzynarodowy',
            en: 'Transport in Countries',
          },
          links: linksCountries,
        },
        {
          slug: '/transport-krajowy/',
          title: {
            pl: 'Transport Krajowy',
            en: 'Transport Krajowy',
          },
        },
        {
          slug: '/transport-branze/',
          title: {
            pl: 'Transport dla Branż',
            en: 'Transport in Industries',
          },
          links: linksIndustires,
        },
        {
          slug: '/transport-rodzaje/',
          title: {
            pl: 'Usługi Transportowe',
            en: 'Transport Types',
          },
          links: linksTransport,
        },
      ],
    },
    {
      slug: '/spedycja/',
      title: {
        pl: 'Spedycja',
        en: 'Forwarding',
      },
      links: linksSpedition,
    },
    {
      slug: '/logistyka/',
      title: {
        pl: 'Logistyka',
        en: 'Logistics',
      },
      links: linksLogistics,
    },
    {
      slug: '/przewoznik/',
      title: {
        pl: 'Przewoźnik',
        en: 'Carrier',
      },
      links: updatedLinksCarrier
    },
    {
      slug: '/wycena-transportu/',
      title: {
        pl: 'Wycena',
        en: 'Quote',
      },
    },
    {
      slug: '/blog/',
      title: {
        pl: 'Blog',
        en: 'News',
      },
      links: linksBlogs,
    },
    {
      slug: '/firma/',
      title: {
        pl: 'Firma',
        en: 'Company',
      },
      links: [
        {
          slug: '/poznaj-nas/',
          title: {
            pl: 'Poznaj Nas',
            en: 'Discover Us',
          },
        },
        {
          slug: '/historia/',
          title: {
            pl: 'Historia',
            en: 'History',
          },
        },
        {
          slug: '/wspolpraca/',
          title: {
            pl: 'Współpraca',
            en: 'Collaboration',
          },
        },
        {
          slug: '/przewoznik/',
          title: {
            pl: 'Strefa Przewoźnika',
            en: `Carrier's Zone`,
          },
          links: [
            {
              slug: '/platnosci/',
              title: {
                pl: 'Płatności',
                en: 'Payments',
              },
            },
          ],
          left: true,
        },
        {
          slug: '/csr/',
          title: {
            pl: 'CSR',
            en: 'CSR',
          },
          links: linksCsr,
          left: true,
        },
        {
          slug: '/nagrody/',
          title: {
            pl: 'Nagrody',
            en: 'Achievements',
          },
          links: linksAchievements,
          left: true,
        },
        {
          slug: '/kariera/',
          title: {
            pl: 'Kariera',
            en: 'Career',
          },
        },
        {
          slug: '/blog/?category=wydarzenia',
          title: {
            pl: 'Wydarzenia',
            en: 'Events',
          },
        },
        {
          slug: '/blog/?category=wiedza',
          title: {
            pl: 'Wiedza',
            en: 'Knowledge',
          },
        },
        {
          slug: '/dla-mediow/',
          title: {
            pl: 'Dla Mediów',
            en: 'For Media',
          },
        },
        {
          slug: '/przetargi/',
          title: {
            pl: 'Przetargi',
            en: '',
          },
        },
        {
          slug: '/prezentacja-omida-vls/',
          title: {
            pl: 'Prezentacja firmy',
            en: 'Company Presentation',
          },
        },
      ],
    },
    {
      slug: '/kontakt/',
      title: {
        pl: 'Kontakt',
        en: 'Contact',
      },
    },
    {
      title: {
        pl: 'PL',
        en: 'PL',
      },
      slug: '/',
      links: [
        {
          name: 'Polski',
          slug: '/',
        },
        {
          name: 'English',
          slug: 'https://omida.eu',
        },
        {
          name: 'Español',
          slug: 'https://omida.es',
        },
        // {
        //   name: 'Română',
        //   slug: 'https://omidalogistics.ro',
        // },
      ],
    },
  ]

  const footer = [
    {
      slug: '/transport/',
      title: {
        pl: 'Transport',
        en: 'Transport',
      },
      links: [
        {
          slug: '/transport-intermodalny/',
          title: {
            pl: 'Transport Intermodalny',
            en: 'Intermodal Transport',
          },
        },
        {
          slug: '/transport-miedzynarodowy/',
          title: {
            pl: 'Transport Międzynarodowy',
            en: 'Transport in Countries',
          },
        },
        {
          slug: '/transport-krajowy/',
          title: {
            pl: 'Transport Krajowy',
            en: 'Transport Krajowy',
          },
        },
        {
          slug: '/transport-branze/',
          title: {
            pl: 'Transport dla Branż',
            en: 'Transport in Industries',
          },
        },
        {
          slug: '/transport-rodzaje/',
          title: {
            pl: 'Rodzaje Transportu',
            en: 'Transport Types',
          },
        },
      ].concat(linksTransport),
    },
    {
      slug: '/logistyka/',
      title: {
        pl: 'Logistyka',
        en: 'Logistics',
      },
      links: [
        {
          slug: '/spedycja/',
          title: {
            pl: 'Spedycja',
            en: 'Forwarding',
          },
        },
      ].concat(linksLogistics),
    },
    {
      slug: '/firma/',
      title: {
        pl: 'Firma',
        en: 'Company',
      },
      links: [
        {
          slug: '/poznaj-nas/',
          title: {
            pl: 'Poznaj Nas',
            en: 'Discover Us',
          },
        },
        {
          slug: '/historia/',
          title: {
            pl: 'Historia',
            en: 'History',
          },
        },
        {
          slug: '/wspolpraca/',
          title: {
            pl: 'Współpraca',
            en: 'Collaboration',
          },
        },
        {
          slug: '/przetargi/',
          title: {
            pl: 'Przetargi',
            en: '',
          },
        },
        {
          slug: '/kariera/',
          title: {
            pl: 'Kariera',
            en: 'Career',
          },
        },
        {
          slug: '/csr/',
          title: {
            pl: 'CSR',
            en: 'CSR',
          },
        },
        {
          slug: '/nagrody/',
          title: {
            pl: 'Nagrody',
            en: 'Achievements',
          },
        },
        {
          slug: '/blog/',
          title: {
            pl: 'Blog',
            en: 'News',
          },
        },
        {
          slug: '/blog/?category=wydarzenia',
          title: {
            pl: 'Wydarzenia',
            en: 'Events',
          },
        },
        {
          slug: '/blog/?category=wiedza',
          title: {
            pl: 'Wiedza',
            en: 'Knowledge',
          },
        },
        {
          slug: '/przewoznik/',
          title: {
            pl: 'Strefa Przewoźnika',
            en: `Carrier's Zone`,
          },
        },
        {
          slug: '/platnosci/',
          title: {
            pl: 'Płatności',
            en: 'Payments',
          },
        },
        {
          slug: '/kontakt/#oddzialy',
          title: {
            pl: 'Oddziały',
            en: 'Offices',
          },
        },
        {
          slug: '/poznaj-nas/#certyfikaty',
          title: {
            pl: 'Certyfikaty',
            en: 'Ceretificates',
          },
        },
        {
          slug: '/dla-mediow/',
          title: {
            pl: 'Dla Mediów',
            en: 'For Media',
          },
        },
        {
          slug: '/prezentacja-omida-vls/',
          title: {
            pl: 'Prezentacja firmy',
            en: 'Company Presentation',
          },
        },
      ],
    },
    {
      slug: '/wycena-transportu/',
      title: {
        pl: 'Wycena Transportu',
        en: 'Transport Quote',
      },
    },

    {
      slug: '/kontakt/',
      title: {
        pl: 'Kontakt',
        en: 'Contact',
      },
    },
  ]

  return { menu, footer }
}

export default useMenu
